import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api, useAuth } from '../contexts/AuthContext';
import { Plus, Search, Edit, Trash2, Cpu } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const Drones = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const initialOrgId = user?.role === 'superadmin' ? '' : user?.org_id;
  const [orgId, setOrgId] = useState(initialOrgId);
  const [deleteDroneId, setDeleteDroneId] = useState(null);
  const [editingDrone, setEditingDrone] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { data: orgsData } = useQuery(
    'organizations',
    () => api.get('/admin/orgs').then(res => res.data),
    {
      enabled: user?.role === 'superadmin'
    }
  );

  const effectiveOrgId = user?.role === 'superadmin' ? orgId : user?.org_id;

  const queryKey = useMemo(() => ['drones', search, effectiveOrgId], [search, effectiveOrgId]);

  const { data, isLoading } = useQuery(
    queryKey,
    async () => {
      const params = new URLSearchParams();
      if (search) params.append('q', search);
      if (effectiveOrgId) params.append('org_id', effectiveOrgId);
      params.append('limit', '100');
      return api.get(`/drones?${params.toString()}`).then((res) => res.data);
    },
    { keepPreviousData: true }
  );

  const drones = data?.drones || [];

  const deleteMutation = useMutation(
    (droneId) => api.delete(`/drones/${droneId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('drones');
        toast.success('Drone excluído com sucesso!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erro ao excluir drone');
      }
    }
  );

  const confirmDeleteDrone = () => {
    if (!deleteDroneId) return;
    deleteMutation.mutate(deleteDroneId, {
      onSettled: () => setDeleteDroneId(null)
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Drones</h1>
          <p className="text-gray-600">Cadastre seus drones e selecione no registro de operação</p>
        </div>
        <button
          onClick={() => {
            setEditingDrone(null);
            setShowModal(true);
          }}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Cadastrar Drone
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por apelido ou prefixo..."
                className="input pl-10"
              />
            </div>
            {user?.role === 'superadmin' && (
              <div className="w-full md:w-64">
                <select
                  className="input"
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                >
                  <option value="">Todas as Empresas</option>
                  {orgsData?.map(org => (
                    <option key={org.id} value={org.id}>{org.name || org.company_name || org.id}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <LoadingSpinner size="lg" />
            </div>
          ) : drones.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Apelido</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modelo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prefixo</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {drones.map((d) => (
                    <tr key={d.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{d.name || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{d.model || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{d.prefix || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => {
                            setEditingDrone(d);
                            setShowModal(true);
                          }}
                          className="text-primary-600 hover:text-primary-900 mr-3"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteDroneId(d.id)}
                          className="text-red-600 hover:text-red-900"
                          disabled={deleteMutation.isLoading}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <Cpu className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-sm font-medium text-gray-900 mb-2">Nenhum drone cadastrado</h3>
              <p className="text-sm text-gray-500 mb-4">
                {search ? 'Tente ajustar os termos de busca' : 'Cadastre seu primeiro drone para selecionar nas operações'}
              </p>
              <button
                onClick={() => {
                  setEditingDrone(null);
                  setShowModal(true);
                }}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Cadastrar Drone
              </button>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <DroneModal
          drone={editingDrone}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            setEditingDrone(null);
            queryClient.invalidateQueries('drones');
          }}
        />
      )}
      {deleteDroneId && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Excluir drone</h3>
                <p className="text-sm text-gray-600">
                  Tem certeza que deseja excluir este drone? Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setDeleteDroneId(null)}
                  disabled={deleteMutation.isLoading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={confirmDeleteDrone}
                  disabled={deleteMutation.isLoading}
                >
                  {deleteMutation.isLoading ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DroneModal = ({ drone, onClose, onSuccess }) => {
  const isEdit = Boolean(drone?.id);
  const [formData, setFormData] = useState({
    name: drone?.name || '',
    prefix: drone?.prefix || '',
    model: drone?.model || ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const formatPrefix = (raw) => {
    const value = String(raw || '').toUpperCase();
    const letters = value.replace(/[^A-Z]/g, '').slice(0, 2);
    const digits = value.replace(/[^0-9]/g, '').slice(0, 9);
    if (!letters && !digits) return '';
    if (letters.length < 2) return letters;
    return `${letters}-${digits}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'prefix') {
      setFormData((prev) => ({ ...prev, prefix: formatPrefix(value) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const prefix = formData.prefix ? formatPrefix(formData.prefix) : '';
    if (prefix && !/^[A-Z]{2}-\d{9}$/.test(prefix)) {
      toast.error('Prefixo inválido. Use o formato AA-000000000');
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        name: formData.name,
        prefix: prefix || undefined,
        model: formData.model?.trim() || undefined
      };
      if (isEdit) {
        await api.put(`/drones/${drone.id}`, payload);
        toast.success('Drone atualizado com sucesso!');
      } else {
        await api.post('/drones', payload);
        toast.success('Drone cadastrado com sucesso!');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar drone');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {isEdit ? 'Editar Drone' : 'Cadastrar Drone'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input
                type="text"
                name="name"
                className="input"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ex: DJI Agras T40"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prefixo *</label>
              <input
                type="text"
                name="prefix"
                required
                className="input"
                value={formData.prefix}
                onChange={handleChange}
                placeholder="Ex: AA-000000000"
                inputMode="text"
                maxLength={12}
                pattern="[A-Za-z]{2}-[0-9]{9}"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
              <input
                type="text"
                name="model"
                className="input"
                value={formData.model}
                onChange={handleChange}
                placeholder="Ex: DJI Agras T40"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={isLoading}>
                Cancelar
              </button>
              <button type="submit" className="btn-primary" disabled={isLoading}>
                {isLoading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Drones;
